import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Grid,
  Alert, // Added Alert
  Typography,
  Button,
} from "@mui/material";
import useFirestore from "../../../../config/fireBaseHook"; // Import useFirestore
import {
  TpHeiDropdown,
  ProgramDeliveryDropdown,
} from "../../CourseCatalog/DropDowns";
import InstructorDropdown from "./InstructorDropdown";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";
import ConfirmDeleteDialog from "../../../common/ConfirmDeleteDialog";
import { formatDateTime } from "../../../../utils/formatDate";

const Edit = ({ open, onClose, deliveryDetail, onRecordUpdated }) => {
  const { updateData: updateFirestoreData, deleteData: deleteFirestoreData } =
    useFirestore("DeliveryDetails"); // Use Firestore hook
  const { data: heiPrograms } = useFirestore("HeiPrograms"); // Fetch HEI programs for CTE code lookup
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  useEffect(() => {
    if (deliveryDetail) {
      // Debug logging for InstructorID
      console.log("Edit form - deliveryDetail received:", {
        InstructorID: deliveryDetail.InstructorID,
        InstructorIDType: typeof deliveryDetail.InstructorID,
        fullDeliveryDetail: deliveryDetail,
      });

      // Firestore may store these checkbox fields as a boolean, a number
      // (-1/0), or a string ("-1"/"0"), depending on how the record was
      // created/imported. Normalize all of them to numeric -1/0 so the
      // checkbox's strict `=== -1` check always matches.
      const normalizeFlag = (value) =>
        value === -1 || value === "-1" || value === true ? -1 : 0;

      setFormData({
        ...deliveryDetail,
        Active: normalizeFlag(deliveryDetail.Active),
        StrandNonactive: normalizeFlag(deliveryDetail.StrandNonactive),
        InstructorNonactiveInStrand: normalizeFlag(
          deliveryDetail.InstructorNonactiveInStrand,
        ),
        SRGradClass: normalizeFlag(deliveryDetail.SRGradClass),
        JRclass: normalizeFlag(deliveryDetail.JRclass),
      });

      // Debug logging after setting formData
      console.log("Edit form - formData set:", {
        InstructorID: deliveryDetail.InstructorID,
        InstructorIDType: typeof deliveryDetail.InstructorID,
      });
    }
  }, [deliveryDetail]);

  // Function to get CTE Program Code from HEI Program Code
  const getCteProgCodeFromHei = (heiProgramCode) => {
    if (!heiPrograms || !heiProgramCode) return "";

    const heiProgram = heiPrograms.find(
      (program) =>
        program.ProgramCode === heiProgramCode ||
        program.HEI_Program_Code === heiProgramCode ||
        program.HEIProgramCode === heiProgramCode
    );

    return heiProgram?.CTE_Program_Code || heiProgram?.CTE_Program_Cd || "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    const updatedData = {
      ...formData,
      [name]: value,
    };

    // If HEI Program Code changed, update CTE Program Code automatically
    if (name === "HEIProgramCode") {
      const cteCode = getCteProgCodeFromHei(value);
      updatedData.CTE_Program_Cd = cteCode;
    }

    setFormData(updatedData);
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: checked ? -1 : 0,
    }));
  };

  const handleSubmit = async () => {
    // Validate required dropdown fields
    if (
      !formData.ProgramDeliveryID ||
      formData.ProgramDeliveryID.trim() === ""
    ) {
      setSnackbarMessage("Program Delivery ID is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (!formData.HEIProgramCode || formData.HEIProgramCode.trim() === "") {
      setSnackbarMessage("HEI Program Code is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (!formData.InstructorID || formData.InstructorID.trim() === "") {
      setSnackbarMessage("Instructor ID is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    setSnackbarMessage("");
    try {
      const { id, ...dataToUpdate } = formData; // Separate id from the rest of the data
      if (!id) {
        throw new Error("Record ID is missing. Cannot update.");
      }
      await updateFirestoreData(id, dataToUpdate); // updateData from useFirestore handles updatedAt
      setSnackbarMessage("Record updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      onRecordUpdated(); // Call the callback function to refresh the list
    } catch (error) {
      console.error("Error updating record:", error);
      setSnackbarMessage(`Error updating record: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setSnackbarMessage("");
    try {
      if (!formData.id) {
        throw new Error("Record ID is missing. Cannot delete.");
      }
      await deleteFirestoreData(formData.id);
      setSnackbarMessage("Record deleted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      onRecordUpdated(); // Call the callback function to refresh the list
      onClose(); // Close the dialog
    } catch (error) {
      console.error("Error deleting record:", error);
      setSnackbarMessage(`Error deleting record: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDeleteOpen = () => {
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDeleteClose = () => {
    setConfirmDeleteOpen(false);
  };

  const handleConfirmDelete = () => {
    handleConfirmDeleteClose();
    handleDelete();
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  // The X (and backdrop/Escape, which share the same Dialog onClose) always
  // asks for confirmation first rather than closing immediately, so an
  // in-progress edit isn't lost by an accidental click.
  const handleCloseAttempt = () => {
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    setConfirmCloseOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseAttempt} maxWidth="md" fullWidth>
        <ModalDialogTitle onClose={handleCloseAttempt}>
          Edit Record
        </ModalDialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <ProgramDeliveryDropdown
                value={formData?.ProgramDeliveryID || ""}
                onChange={handleChange}
                name="ProgramDeliveryID"
                label="Program Delivery ID"
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TpHeiDropdown
                value={formData?.HEIProgramCode || ""}
                onChange={handleChange}
                name="HEIProgramCode"
                label="HEI Program Code"
                fullWidth
                size="medium"
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <InstructorDropdown
                value={formData?.InstructorID || ""}
                onChange={handleChange}
                name="InstructorID"
                label="Instructor ID"
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="CTE Program Code"
                name="CTE_Program_Cd"
                value={formData?.CTE_Program_Cd || ""}
                fullWidth
                margin="normal"
                disabled
                helperText="Auto-populated based on HEI Program Code selection"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="MVTPC Strand"
                name="MVTPCstrand"
                value={formData?.MVTPCstrand || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Notes"
                name="Notes"
                value={formData?.Notes || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Career Field Code"
                name="Career_Field_Cd"
                value={formData?.Career_Field_Cd || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.Active === -1}
                    onChange={handleCheckboxChange} // This correctly sets -1 or 0
                    name="Active"
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.StrandNonactive === -1}
                    onChange={handleCheckboxChange}
                    name="StrandNonactive"
                  />
                }
                label="Strand Nonactive"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.InstructorNonactiveInStrand === -1}
                    onChange={handleCheckboxChange}
                    name="InstructorNonactiveInStrand"
                  />
                }
                label="Instructor Nonactive In Strand"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.SRGradClass === -1}
                    onChange={handleCheckboxChange}
                    name="SRGradClass"
                  />
                }
                label="SR Grad Class"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.JRclass === -1}
                    onChange={handleCheckboxChange}
                    name="JRclass"
                  />
                }
                label="JR Class"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Created At"
                value={formatDateTime(formData?.created_at) || "—"}
                fullWidth
                margin="normal"
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Updated At"
                value={formatDateTime(formData?.updatedAt) || "—"}
                fullWidth
                margin="normal"
                disabled
              />
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions
          onSave={handleSubmit}
          saving={loading}
          onDelete={handleConfirmDeleteOpen}
          deleting={loading}
        />
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
            variant="filled"
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Dialog>
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={handleConfirmDeleteClose}
        onConfirm={handleConfirmDelete}
        deleting={loading}
        message="Are you sure you want to delete this?"
      />
      <Dialog
        open={confirmCloseOpen}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to close before saving your changes?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelClose}>No</Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            Yes, Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Edit;
